import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Menu, X, ChevronDown, Youtube } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { CATEGORIES } from "@/lib/api";

export const Logo = ({ className = "h-9" }) => (
  <img src="/brand/logo-dark.png" alt="MROUE CANVAS — M. Mroué" className={`${className} w-auto select-none`} draggable={false} />
);

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center font-mono text-[11px] tracking-[0.2em]" data-testid="language-switch">
      {["en", "fr"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="mx-1.5 text-white/30">/</span>}
          <button data-testid={`language-switch-${l}`} onClick={() => setLang(l)}
            className={`uppercase transition-colors ${lang === l ? "text-white" : "text-white/45 hover:text-white"}`}>{l}</button>
        </span>
      ))}
    </div>
  );
};

const GalleryMenu = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid="nav-gallery-toggle" onClick={() => setOpen((o) => !o)} className="link-u flex items-center gap-1 py-2">
        {t("gallery")} <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.25 }}
            className="absolute left-1/2 top-full -translate-x-1/2 pt-3">
            <div className="min-w-[220px] border border-white/10 bg-[#111]/95 p-2 backdrop-blur-xl">
              {CATEGORIES.map((c) => (
                <Link key={c} to={`/gallery/${c}`} data-testid={`nav-gallery-${c}`} onClick={() => setOpen(false)}
                  className="block px-4 py-2.5 text-sm text-white/75 transition-colors hover:bg-white/5 hover:text-white">{t(c)}</Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const NAV = [["video", "/video"], ["frame", "/frame-visualizer"], ["blog", "/blog"], ["about", "/about"], ["contact", "/contact"]];

export const Header = () => {
  const { t, settings } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  const last = useRef(loc.pathname);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { if (last.current !== loc.pathname) { setMobile(false); last.current = loc.pathname; } }, [loc.pathname]);
  const ig = `https://www.instagram.com/${settings.instagram_handle || ""}`;
  return (
    <>
      <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${scrolled ? "border-b border-white/[0.06] bg-[#0b0b0b]/70 backdrop-blur-xl" : "border-b border-transparent"}`}>
        <div className="mx-auto flex h-[76px] max-w-[1600px] items-center justify-between px-5 md:px-10">
          <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo className="h-8 md:h-9" /></Link>
          <nav className="hidden items-center gap-8 text-[13px] tracking-wide text-white/85 lg:flex">
            <NavLink to="/" end data-testid="nav-link-home" className={({ isActive }) => `link-u py-2 ${isActive ? "active text-white" : ""}`}>{t("home")}</NavLink>
            <GalleryMenu />
            {NAV.map(([k, to]) => (
              <NavLink key={k} to={to} data-testid={`nav-link-${k}`} className={({ isActive }) => `link-u py-2 ${isActive ? "active text-white" : ""}`}>{t(k)}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <a href={ig} target="_blank" rel="noreferrer" data-testid="nav-instagram" aria-label="Instagram" className="hidden text-white/80 transition-colors hover:text-white sm:block"><Instagram size={17} /></a>
            {settings.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noreferrer" data-testid="nav-youtube" aria-label="YouTube" className="hidden text-white/80 hover:text-white sm:block"><Youtube size={18} /></a>}
            <LangSwitch />
            <button data-testid="nav-mobile-toggle" onClick={() => setMobile((m) => !m)} className="text-white lg:hidden" aria-label="Menu">{mobile ? <X /> : <Menu />}</button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {mobile && (
          <motion.div data-testid="mobile-menu" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }} className="fixed inset-0 z-40 overflow-y-auto bg-[#0b0b0b] px-6 pb-10 pt-28 lg:hidden">
            <div className="flex flex-col gap-1">
              {[["home", "/"], ...CATEGORIES.map((c) => [c, `/gallery/${c}`]), ...NAV, ["instagram", "/instagram"]].map(([k, to], i) => (
                <motion.div key={k} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.04 }}>
                  <Link to={to} data-testid={`mobile-nav-${k}`} className="font-display block py-2 text-3xl font-bold text-white">{t(k)}</Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
